import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Long-form text that survives its component.
 *
 * A `useState("")` draft dies with the component instance, and in this app a
 * component dies for reasons the typist never caused: a poll that failed while
 * the tab was backgrounded, a question dropping out of a filtered list because
 * someone answered it in Slack, a stray backdrop click, a browser tab discard.
 * Minutes of typing went with it. Mirroring the draft to ``localStorage`` makes
 * the unmount survivable — reopening restores the text.
 *
 * Keys are namespaced ``dux-draft:<surface>:<id>``. Prefer an id that is stable
 * across the surfaces rendering the same thing (a question id, not a list
 * position), so the draft follows its subject rather than one mount point.
 */
const PREFIX = "dux-draft:";
const WRITE_DEBOUNCE_MS = 300;
/** Drafts older than this are dead weight — nobody resumes a month-old answer. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  text: string;
  savedAt: number;
}

/** Build a namespaced key. Segments are joined with ":" — ids are not escaped. */
export function draftKey(...segments: (string | number)[]): string {
  return PREFIX + segments.join(":");
}

function read(key: string, nowMs: number): string {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    if (typeof parsed?.text !== "string") return "";
    if (typeof parsed.savedAt === "number" && nowMs - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(key);
      return "";
    }
    return parsed.text;
  } catch {
    // Unparseable, or storage unavailable (Safari private mode, quota).
    return "";
  }
}

function write(key: string, text: string): void {
  try {
    if (text) {
      const payload: StoredDraft = { text, savedAt: Date.now() };
      window.localStorage.setItem(key, JSON.stringify(payload));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Quota or private mode — the in-memory draft still works.
  }
}

/**
 * Drop expired drafts. Unlike the composer keys this replaces, answer keys are
 * per question id — unbounded over time, so every answered question would leave
 * a key behind forever without a sweep.
 */
function sweep(nowMs: number): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(PREFIX)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as Partial<StoredDraft>;
        const savedAt = typeof parsed?.savedAt === "number" ? parsed.savedAt : 0;
        if (nowMs - savedAt > TTL_MS) stale.push(key);
      } catch {
        stale.push(key); // Malformed: it will never restore, so it is garbage.
      }
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing to sweep.
  }
}

let swept = false;

export interface Draft {
  draft: string;
  setDraft: (next: string) => void;
  /** Drop the text and the stored key — call on a *successful* send, not before. */
  clearDraft: () => void;
  /** True when non-empty text came back from storage, for a "restored" hint. */
  restored: boolean;
}

interface DraftState {
  /** The key this text belongs to — carried in state so a key change can never
   *  mirror the previous surface's text into the new key. */
  key: string;
  text: string;
  restored: boolean;
}

function load(key: string, nowMs: number): DraftState {
  const text = read(key, nowMs);
  return { key, text, restored: text.length > 0 };
}

export function useDraft(key: string): Draft {
  // Captured once per mount: the TTL is day-granular, so a ticking clock would
  // buy nothing, and Date.now() in render is impure under the compiler.
  const [nowMs] = useState(() => Date.now());
  const [state, setState] = useState<DraftState>(() => load(key, nowMs));

  // One sweep per page load is enough — this is housekeeping, not correctness.
  useEffect(() => {
    if (swept) return;
    swept = true;
    sweep(nowMs);
  }, [nowMs]);

  // Re-point at a different draft when the key changes (e.g. a new comment
  // anchor). Derived during render rather than in an effect, so the mirror
  // below never observes a (new key, old text) pair.
  const current = state.key === key ? state : load(key, nowMs);

  // Mirror to storage, debounced so a fast typist writes once per pause.
  const { key: liveKey, text } = current;
  useEffect(() => {
    const id = window.setTimeout(() => write(liveKey, text), WRITE_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [text, liveKey]);

  // Flush on unmount. The debounce above cancels its pending write when the
  // component goes away — and an unmount is precisely the event this hook
  // exists to survive, so the last keystrokes before it are the ones least
  // affordable to drop. Latest values come from a ref because this effect
  // must run on unmount only, never on a dependency change.
  //
  // `cleared` makes a sent draft stay sent. A successful send usually clears
  // and unmounts in the same breath (the row leaves the list, the composer
  // closes), and without this flag the unmount flush could write the just-sent
  // text back and resurrect it on the next visit.
  const latest = useRef({ key: liveKey, text, cleared: false });
  useEffect(() => {
    latest.current = { key: liveKey, text, cleared: false };
  }, [liveKey, text]);
  useEffect(
    () => () => {
      if (latest.current.cleared) return;
      write(latest.current.key, latest.current.text);
    },
    [],
  );

  // Closed over the current `key` rather than a ref — these are handed to
  // change handlers, not to memo dependency lists, so a per-render identity
  // costs nothing and keeps the key and the text provably in step.
  const setDraft = useCallback(
    (next: string) => {
      setState({ key, text: next, restored: false });
    },
    [key],
  );

  const clearDraft = useCallback(() => {
    latest.current = { key, text: "", cleared: true };
    setState({ key, text: "", restored: false });
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  return {
    draft: current.text,
    setDraft,
    clearDraft,
    restored: current.restored,
  };
}
